<template>
    <div id="optionsRow">
        <div class="optionsRowColumn">
            <CqSelectInput
                label="Kies rooster"
                :options="rosters"
                v-model="selectedRoster"
            />
        </div>

        <div class="optionsRowColumn backButtons">
            <CqButton
                @click="jumpSelectedDaysBack"
                shape="flat"
                tooltip="Naar de vorige periode"
                icon="to-start"
                margin="none"
                size="auto"
                :disabled="!rosterLoaded || !canJumpBackSelectedDays"
            />

            <CqButton
                @click="jumpWeekBack"
                shape="flat"
                tooltip="Naar de vorige week"
                icon="back"
                margin="none"
                size="auto"
                :disabled="!rosterLoaded || !canJumpBackOneWeek"
            />
        </div>

        <div class="optionsRowColumn">
            <CustomDateSelect
                class="optionsRowDatePicker"
                v-model="fromDate"
                :disabled="!rosterLoaded"
                label="Vanaf datum"
                required
                showRosterStatus
                :roster="roster"
                :disableTodayButton="todayIsVisibleInPeriod"
                noMargin
            />
        </div>

        <div class="optionsRowColumn forwardButtons">
            <CqButton
                @click="jumpWeekForward"
                shape="flat"
                tooltip="Naar de volgende week"
                icon="foreward"
                margin="none"
                size="auto"
                :disabled="!rosterLoaded || !canJumpForwardOneWeek"
            />

            <CqButton
                @click="jumpSelectedDaysForward"
                shape="flat"
                tooltip="Naar de volgende periode"
                icon="to-end"
                margin="none"
                size="auto"
                :disabled="!rosterLoaded || !canJumpForwardSelectedDays"
            />
        </div>

        <div class="optionsRowColumn">
            <CqSelectInput
                label="Getoonde dagen"
                :options="daysVisibleOptions"
                v-model="visibleDays"
            />
        </div>
        <!-- CustomDaysVisibleModal component -->
        <CustomDaysVisibleModal :show="showCustomDaysModel" :modelValue="customDaysVisible" @update:modelValue="updateCustomDaysVisible" />
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Model, Prop, Watch} from 'vue-facing-decorator';
    import CustomDaysVisibleModal from '@/components/modals/CustomDaysVisibleModal.vue';
    import {CqButton, CqSelectInput, NumberSelectInputOption} from '@cquential/ui';
    import CustomDateSelect from '@/components/Branding/CustomDateSelect.vue';
    import {RosterViewSettings} from '@/types';
    import {RosterData, RosterSelectOption, NullableRange} from '@/types/types';
    import {DEFAULT_VISIBLE_DAYS} from '@/util/Defaults';
    import Dates from '@cquential/date-utils';
    import {StartDateDefaultType} from '@/types/enums/StartDateDefaultType';

    @Component({
        components: {
            CustomDaysVisibleModal,
            CqButton,
            CqSelectInput,
            CustomDateSelect,
        },
        emits: ['update:preference'],
    })
    export default class OptionsRow extends Vue {
        @Model({required: true}) public rosterViewSettings!: RosterViewSettings;
        @Prop() public roster?: RosterData;
        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({required: true}) public dateInputRange!: NullableRange<Date>;
        @Prop({required: true}) public preferences!: Record<string, string | number>;

        private selectedCustomDaysVisible: number = -1;
        private showCustomDaysModel: boolean = false;
        private readonly DEFAULT_VISIBLE_DAYS_OPTIONS: number[] = [7, 14, 21, 28];

        @Watch('rosterViewSettings')
        private onRosterViewSettingsChanged(newValue: RosterViewSettings): void {
            this.checkIfSelectedVisibleDaysIsACustomValue(newValue.visibleDays);
        }

        public mounted(): void {
            this.checkIfSelectedVisibleDaysIsACustomValue(this.rosterViewSettings.visibleDays);
        }

        private checkIfSelectedVisibleDaysIsACustomValue(days: number): void {
            if (this.customDaysVisible !== days && !this.DEFAULT_VISIBLE_DAYS_OPTIONS.includes(days)) {
                this.customDaysVisible = days;
            }
        }

        public updateCustomDaysVisible(value: number): void {
            this.showCustomDaysModel = false;
            if (value <= 0) {
                this.resetDaysVisibleOptionsSelect();
            } else {
                this.customDaysVisible = value;
            }
        }

        public get customDaysVisible(): number {
            if (this.selectedCustomDaysVisible === -1) {
                return 'customDaysVisible' in this.preferences ? this.preferences.customDaysVisible as number : DEFAULT_VISIBLE_DAYS;
            }

            return this.selectedCustomDaysVisible;
        }

        public set customDaysVisible(val: number) {
            this.visibleDays = val;
            this.selectedCustomDaysVisible = val;
            this.$emit('update:preference', 'customDaysVisible', val);
        }

        public get visibleDays(): number {
            return this.rosterViewSettings.visibleDays;
        }

        public set visibleDays(val: number) {
            if (val <= 0) {
                this.showCustomDaysModel = true;

                return;
            }

            this.rosterViewSettings = {...this.rosterViewSettings, visibleDays: val};
        }

        public get rosterLoaded(): boolean {
            return this.roster !== undefined;
        }

        private resetDaysVisibleOptionsSelect(): void {
            this.visibleDays = 'lastChosenDaysVisible' in this.preferences ? this.preferences.lastChosenDaysVisible as number : DEFAULT_VISIBLE_DAYS;
        }

        public get canJumpBackOneWeek(): boolean {
            return this.dateInputRange.min === null || Dates.Utils.addDays(this.fromDate, -7) >= this.dateInputRange.min;
        }

        public get canJumpBackSelectedDays(): boolean {
            return this.dateInputRange.min === null || Dates.Utils.addDays(this.fromDate, -this.visibleDays) >= this.dateInputRange.min;
        }

        public get canJumpForwardOneWeek(): boolean {
            return this.dateInputRange.max === null || Dates.Utils.addDays(this.fromDate, 7) <= this.dateInputRange.max;
        }

        public get canJumpForwardSelectedDays(): boolean {
            return this.dateInputRange.max === null || Dates.Utils.addDays(this.fromDate, this.visibleDays) <= this.dateInputRange.max;
        }

        public get selectedRoster(): number {
            return this.rosterViewSettings.rosterId;
        }

        public set selectedRoster(val: number) {
            const noRosterSelectedBefore: boolean = this.rosterViewSettings.rosterId === 0;
            const newRosterViewSettings: RosterViewSettings = {...this.rosterViewSettings, rosterId: val};

            for (const roster of this.rosters) {
                if (roster.data.id === val) {
                    if (noRosterSelectedBefore
                        && 'startDateDefaultType' in this.preferences
                        && (this.preferences.startDateDefaultType as StartDateDefaultType) === StartDateDefaultType.FirstDayNotPublished
                        && roster.data.publishedUntil
                    ) {
                        newRosterViewSettings.startDate = Dates.Utils.addDays(roster.data.publishedUntil, 1);
                    }

                    if (roster.data.beginDate !== null && this.rosterViewSettings !== null) { // neither is likely to be null, but still check
                        newRosterViewSettings.startDate = roster.data.beginDate > this.rosterViewSettings.startDate ? new Date(roster.data.beginDate) : newRosterViewSettings.startDate;
                    } else {
                        // unlikely, but we need to do SOMETHING that we _know_ is valid
                        // props is probably not the behaviour the user is looking for, but at least it will not be a hard error
                        newRosterViewSettings.startDate = roster.data.proposedUntil ? Dates.Utils.addDays(roster.data.proposedUntil, 1) : Dates.Utils.addDays(Dates.Utils.today(), -3);
                    }

                    break;
                }
            }

            this.rosterViewSettings = newRosterViewSettings;
        }

        public get fromDate(): Date {
            return this.rosterViewSettings.startDate;
        }

        public set fromDate(date: Date) {
            this.rosterViewSettings = {...this.rosterViewSettings, startDate: date};
        }

        public get daysVisibleOptions(): NumberSelectInputOption[] {
            const result: NumberSelectInputOption[] = [];
            for (const option of this.DEFAULT_VISIBLE_DAYS_OPTIONS) {
                result.push({
                    label: `Toon ${option} dagen`,
                    value: option,
                });
            }

            result.push({
                label: 'Zelf ingesteld: ' + this.customDaysVisible + ' dagen',
                value: this.customDaysVisible,
            });

            result.push({
                label: 'Anders',
                value: 0,
            });

            return result;
        }

        public get todayIsVisibleInPeriod(): boolean {
            const endDate: Date = Dates.Utils.addDays(this.fromDate, this.visibleDays - 1);

            return Dates.compare(Dates.Utils.today()).isBetweenDatesInclusive(this.fromDate, endDate);
        }

        public jumpSelectedDaysBack(): void {
            this.fromDate = Dates.Utils.addDays(this.fromDate, -this.visibleDays);
        }

        public jumpSelectedDaysForward(): void {
            this.fromDate = Dates.Utils.addDays(this.fromDate, this.visibleDays);
        }

        public jumpWeekBack(): void {
            this.fromDate = Dates.Utils.addDays(this.fromDate, -7);
        }

        public jumpWeekForward(): void {
            this.fromDate = Dates.Utils.addDays(this.fromDate, 7);
        }
    }
</script>

<style lang="scss" scoped>
    #optionsRow {
        display: grid;
        grid-template-columns: 3fr 1fr 4fr 1fr 3fr;
        gap: 15px;
        height: var(--optionsrow-height);
        margin: 15px 15px 0;

        .optionsRowColumn {
            max-height: var(--optionsrow-height);
        }

        .backButtons {
            text-align: right;
        }

        .optionsRowDatePicker {
            height: var(--optionsrow-height);
        }
    }
</style>
